import React, { FunctionComponent, useMemo, useState } from "react";
import styles from "./styles.module.scss";
import { Grid, ListItemIcon, Menu, MenuItem, Skeleton } from "@mui/material";
import { strings } from "../../../../localization/LocalizedStrings";
import { ModifyEquipmentDialog } from "../ModifyEquipmentDialog";
import { AttributeDesktop } from "../../AttributeDesktop";
import { toast } from "react-toastify";
import { useContextMenu } from "../../../../hooks/contextMenu";
import { attributeIconMap } from "../../../../models/AttributeUtilities";
import { AttributeType } from "../../AttributeList";
import { Icon, IconType } from "../../../../icons";
import { useResettableDialog } from "../../../../hooks/resettableDialog/useResettableDialog";
import { EquipmentCard } from "../EquipmentCard";
import { IEquipment } from "../../../../hooks/equipments";
import { useGetEquipments } from "../../../../hooks/equipments/useGetEquipments";

export const EquipmentsDesktop: FunctionComponent<IEquipmentsDesktopProps> = ({
  handleClose,
  columnWidth,
  preselectedId,
  onEquipmentSelected,
  onClearValue
}) => {
  const [loading, setLoading] = useState(true);
  const [equipments, resetEquipments, setEquipments, noMoreEquipments] =
    useGetEquipments(setLoading);
  const {
    state: modifyDialogState,
    key: modifyDialogKey,
    openAdd,
    openEdit,
    close: closeModifyDialog
  } = useResettableDialog<IEquipment>();
  const [searchText, setSearchText] = useState("");
  const [contextEquipment, setContextEquipment] = useState<IEquipment>();
  const { menuProps, handleContextMenu, handleMenuClose } = useContextMenu();
  const filteredEquipments = useMemo(() => {
    if (searchText == "") return equipments;
    return equipments?.filter(equipment => {
      return (
        equipment.name.toLowerCase().includes(searchText.toLowerCase()) ||
        equipment.description.toLowerCase().includes(searchText.toLowerCase())
      );
    });
  }, [equipments, searchText]);
  return (
    <AttributeDesktop
      handleClose={handleClose}
      isPublic={false}
      items={filteredEquipments}
      emptyIconType={attributeIconMap[AttributeType.EQUIPMENTS]}
      emptyString={strings.empty_equipments}
      addItemString={strings.add_attribute}
      title={strings.equipments_title}
      loading={loading}
      setLoading={setLoading}
      onSearchRequested={searchText => setSearchText(searchText)}
      addAction={openAdd}
      refreshAction={resetEquipments}
      onClearValue={onClearValue}
    >
      {filteredEquipments?.map(equipment => (
        <Grid item key={equipment.uuid} xs={columnWidth}>
          <EquipmentCard
            equipment={equipment}
            selected={preselectedId === equipment.uuid}
            onClick={() => {
              if (onEquipmentSelected) {
                onEquipmentSelected(equipment);
              } else {
                openEdit(equipment);
              }
            }}
            onContextMenu={event => {
              setContextEquipment(equipment);
              handleContextMenu(event);
            }}
          />
        </Grid>
      ))}
      {(loading || !noMoreEquipments) &&
        Array.from({ length: 20 }, (_value, index) => (
          <Grid key={index} item xs={columnWidth}>
            <Skeleton variant={"rectangular"} sx={{ height: 70 }} className={styles.skeleton} />
          </Grid>
        ))}
      <Menu {...menuProps}>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            openEdit(contextEquipment);
          }}
          dense
        >
          <ListItemIcon>
            <Icon type={IconType.EditSquare} />
          </ListItemIcon>
          {strings.alert_modify}
        </MenuItem>
      </Menu>
      <ModifyEquipmentDialog
        key={modifyDialogKey}
        open={modifyDialogState.mode !== "closed"}
        onClose={closeModifyDialog}
        equipment={modifyDialogState.item}
        onNewItem={newEquipment => {
          const newEquipments = [...equipments, newEquipment];
          newEquipments.sort(
            (a, b) =>
              a.status.localeCompare(b.status, undefined, { ignorePunctuation: true }) ||
              a.name.localeCompare(b.name, undefined, { ignorePunctuation: true })
          );
          setEquipments(newEquipments);
          toast.success(strings.generic_update_success);
        }}
        onModifiedItem={newEquipment => {
          const newEquipments = equipments.map(equipment =>
            equipment.uuid == newEquipment.uuid ? newEquipment : equipment
          );
          newEquipments.sort(
            (a, b) =>
              a.status.localeCompare(b.status, undefined, { ignorePunctuation: true }) ||
              a.name.localeCompare(b.name, undefined, { ignorePunctuation: true })
          );
          setEquipments(newEquipments);
          toast.success(strings.generic_update_success);
        }}
        onDeletedItem={deletedEquipment => {
          const newEquipments = equipments.filter(
            equipment => equipment.uuid != deletedEquipment.uuid
          );
          setEquipments(newEquipments);
          toast.success(strings.generic_update_success);
        }}
      />
    </AttributeDesktop>
  );
};

export interface IEquipmentsDesktopProps {
  handleClose?: () => void;
  columnWidth: number;
  preselectedId?: string;
  onEquipmentSelected?: (equipment: IEquipment) => void;
  onClearValue?: () => void;
}
